/**
 * Event Property Exclusion Map
 * =============================
 * Controls which properties are excluded from specific events.
 * Properties listed here will be removed from the event payload
 * BEFORE sending to analytics destinations (RudderStack, Firebase, etc.)
 *
 * Why: The shared `trackEvent()` hook assembles a full property set for all events.
 * Not every event needs every property. This map enables surgical cleanup
 * per event without affecting other events.
 *
 * How to use:
 * 1. Add the event name as a key
 * 2. List property keys to exclude as the value array
 * 3. Unconfigured events keep ALL properties (backward compatible)
 */

import { EventNamesEnum, EventPropertiesEnum } from "@/constants/constants";

/**
 * Properties to exclude per event.
 * Key: EventNamesEnum value (without platform suffix — applied before platform append)
 * Value: Array of property keys to remove from the event payload
 */
export const EVENT_PROPERTY_EXCLUSIONS: Readonly<
  Partial<Record<EventNamesEnum, readonly string[]>>
> = Object.freeze({
  /**
   * THUMBNAIL_VIEWED — High volume (37.8M/90d)
   * Validated against RAW_PROD.EVENTS_WEB.THUMBNAIL_VIEWED_WEB
   */
  [EventNamesEnum.THUMBNAIL_VIEWED]: Object.freeze([
    // Identity duplicates (keep user_id and mobileNumber)
    EventPropertiesEnum.CUSTOMER_USER_ID, // duplicate of user_id
    EventPropertiesEnum.EXTERNAL_ID, // duplicate of user_id
    EventPropertiesEnum.PH, // "91" + mobileNumber — duplicate, India only
    EventPropertiesEnum.USER_ID_LEGACY, // legacy camelCase duplicate of user_id

    // Content duplicates (keep content_id for joins)
    EventPropertiesEnum.LATEST_CONTENT_ID, // acquisition slug, not content-specific
    EventPropertiesEnum.ECOMMERCE, // JSON wrapper around content_ids

    // Content properties derivable from dim_content via content_id join
    EventPropertiesEnum.CONTENT_NAME, // derivable from dim_content.content_title
    EventPropertiesEnum.CONTENT_DIALECT, // derivable from dim_content.content_dialect
    EventPropertiesEnum.SHOW_ID, // derivable from dim_content.show_id

    // Zero-variance / not actionable for thumbnail analytics
    EventPropertiesEnum.APP_LANGUAGE, // not actionable on this event (96% en/hi + garbage values)
    EventPropertiesEnum.COUNTRY, // India only — always "IN"
    EventPropertiesEnum.CURRENCY, // always "INR" — zero variance
    EventPropertiesEnum.NEXT_JS_WEBSITE, // framework artifact — always true
    EventPropertiesEnum.IP_ADDRESS, // not needed for thumbnail analytics
    EventPropertiesEnum.SDK_VERSION, // always "0.1.0" — SDK constant

    // Redundant properties
    EventPropertiesEnum.DEVICE_SCREEN_TYPE, // redundant with device_type (same value, different casing)
    EventPropertiesEnum.CONTENT_CULTURE, // dialect.slice(0,3) — derivable from dialect
    EventPropertiesEnum.INITIAL_USER_CULTURE, // 17.7% filled, redundant with dialect

    // User attributes (available via dim_users join — not needed per event)
    EventPropertiesEnum.MANDATE_STATUS, // user attribute — available via dim_users
    EventPropertiesEnum.USER_CULTURE, // 77% null, redundant with dialect for logged-in users

    // Dead properties (100% NULL on Snowflake — never populated for thumbnails)
    EventPropertiesEnum.APP_DISPLAY_LANGUAGE, // 0% filled — dead
    EventPropertiesEnum.PAGE_TITLE, // 0% filled — dead (page_type already captures page context)
    EventPropertiesEnum.ITEM_ID, // 0% filled — never instrumented

    // Facebook identifiers (not needed — thumbnail_viewed doesn't send to FB Pixel)
    EventPropertiesEnum.FBC, // Facebook click ID
    EventPropertiesEnum.FBP, // Facebook browser ID
  ]),

  /**
   * THUMBNAIL_CLICKED — Same schema as THUMBNAIL_VIEWED
   * Both events fire from the same component with identical property needs
   */
  [EventNamesEnum.THUMBNAIL_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.CONTENT_DIALECT,
    EventPropertiesEnum.SHOW_ID,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.APP_DISPLAY_LANGUAGE,
    EventPropertiesEnum.PAGE_TITLE,
    EventPropertiesEnum.ITEM_ID,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
  ]),

  /**
   * PAGE_VIEWED — High volume (17.5M/90d)
   * Validated against RAW_PROD.EVENTS_WEB.PAGE_VIEWED_WEB
   * ContentDetailPageTv sends to Facebook (sendTo: [FACEBOOK]) — keep FB-critical props
   * Receives Statsig experiment values (in statsigEvents array)
   */
  [EventNamesEnum.PAGE_VIEWED]: Object.freeze([
    // Identity duplicates (keep user_id and mobileNumber)
    EventPropertiesEnum.CUSTOMER_USER_ID, // duplicate of user_id
    EventPropertiesEnum.EXTERNAL_ID, // duplicate of user_id
    EventPropertiesEnum.PH, // "91" + mobileNumber — duplicate, India only
    EventPropertiesEnum.USER_ID_LEGACY, // legacy camelCase duplicate of user_id

    // Content properties — KEEP content_slug, content_title, content_type (TV sends to FB)
    EventPropertiesEnum.CONTENT_ID, // pages don't have content_id (content_ids used instead)

    // Acquisition/GA wrappers (no GA on this event)
    EventPropertiesEnum.LATEST_CONTENT_ID, // acquisition slug, not page-specific
    EventPropertiesEnum.ECOMMERCE, // GA4 wrapper — no GA destination

    // Zero-variance / not actionable for page analytics
    EventPropertiesEnum.APP_LANGUAGE, // garbage values: "logout", "paywall", "genre"
    EventPropertiesEnum.COUNTRY, // 92% India — low variance
    EventPropertiesEnum.CURRENCY, // always "INR" — zero variance
    EventPropertiesEnum.NEXT_JS_WEBSITE, // framework artifact — always true
    EventPropertiesEnum.IP_ADDRESS, // not needed for page analytics
    EventPropertiesEnum.SDK_VERSION, // always "0.1.0" — SDK constant
    EventPropertiesEnum.CHANNEL, // always "web" — zero variance

    // Redundant properties
    EventPropertiesEnum.DEVICE_SCREEN_TYPE, // redundant with device_type (same value, different casing)
    EventPropertiesEnum.CONTENT_CULTURE, // dialect.slice(0,3) — derivable from dialect
    EventPropertiesEnum.INITIAL_USER_CULTURE, // 8.4% filled, redundant with dialect

    // User attributes (available via dim_users join — not needed per event)
    EventPropertiesEnum.MANDATE_STATUS, // user attribute — available via dim_users
    EventPropertiesEnum.USER_CULTURE, // 9.6% fill, redundant with dialect

    // Dead properties (0% fill — never populated for page views)
    EventPropertiesEnum.APP_DISPLAY_LANGUAGE, // 0% filled — dead
    EventPropertiesEnum.PAGE_TITLE, // 0% filled — dead (page_name captures page context)
    EventPropertiesEnum.ITEM_ID, // 0% filled — never instrumented
    "page_id", // 0% filled — dead
    "page_format", // 0% filled — dead
    "genre_id", // 0% filled — dead
    "genre_name", // 0% filled — dead
    "experiment_id", // 0% filled — dead
    "lang", // 0% filled — dead (app_language used instead)
    "extention", // 0% filled — dead (~23 rows)
    "ls_extention", // 0% filled — dead (~23 rows)
    "plan_id", // 0% filled — dead (plan_id_displayed used instead)
    "plan_price", // 0% filled — dead
    "previous_page", // 0% filled — dead
    "source_widget_title", // 0% filled — dead
    "source_widget_position", // 0% filled — dead
    "payment_gateway_displayed", // 0% filled — dead
    "payment_option_displayed", // 0% filled — dead
    "is_amplitude_event", // metadata flag — not needed

    // Not useful for page analytics
    "asset_type", // JSON array, not actionable for page views

    // KEPT: fbc, fbp — Facebook identifiers needed for TV FB Pixel send
    // KEPT: content_slug, content_title, content_type — TV sends to FB
  ]),

  /**
   * WIDGET_VIEWED — High volume (7.6M/90d web)
   * Validated against RAW_PROD.EVENTS_WEB.WIDGET_VIEWED_WEB
   * No Facebook/GA sends — fired via useElementEventsTracker hook
   * Call sites: CarouselCSR.tsx, ContentGridItem.tsx, Element.tsx, ContentRowItem.tsx,
   *             SimilarContentTv.tsx, ItemGrid.tsx (all via useElementEventsTracker)
   */
  [EventNamesEnum.WIDGET_VIEWED]: Object.freeze([
    // Identity duplicates (keep user_id and mobileNumber)
    EventPropertiesEnum.CUSTOMER_USER_ID, // duplicate of user_id (30% fill)
    EventPropertiesEnum.EXTERNAL_ID, // duplicate of user_id
    EventPropertiesEnum.PH, // "91" + mobileNumber — duplicate, India only
    EventPropertiesEnum.USER_ID_LEGACY, // legacy camelCase duplicate of user_id

    // Content duplicates (keep content_id for joins, content_ids for acquisition)
    EventPropertiesEnum.LATEST_CONTENT_ID, // acquisition slug (14% fill), not widget-specific
    EventPropertiesEnum.ECOMMERCE, // GA4 wrapper — no GA destination

    // Zero-variance / not actionable for widget analytics
    EventPropertiesEnum.APP_LANGUAGE, // garbage values: "logout", "paywall", "genre"
    EventPropertiesEnum.COUNTRY, // India only — near zero-variance
    EventPropertiesEnum.CURRENCY, // always "INR" — zero variance
    EventPropertiesEnum.NEXT_JS_WEBSITE, // framework artifact — always true
    EventPropertiesEnum.IP_ADDRESS, // not needed for widget analytics
    EventPropertiesEnum.SDK_VERSION, // always "0.1.0" — SDK constant
    EventPropertiesEnum.CHANNEL, // always "web" — zero variance on web table

    // Redundant properties
    EventPropertiesEnum.DEVICE_SCREEN_TYPE, // redundant with device_type (same value, different casing)
    EventPropertiesEnum.CONTENT_CULTURE, // dialect.slice(0,3) — derivable from dialect
    EventPropertiesEnum.INITIAL_USER_CULTURE, // redundant with dialect

    // User attributes (available via dim_users join — not needed per event)
    EventPropertiesEnum.MANDATE_STATUS, // user attribute — available via dim_users
    EventPropertiesEnum.USER_CULTURE, // redundant with dialect

    // Dead properties (0% fill on Snowflake — never populated for widgets)
    EventPropertiesEnum.APP_DISPLAY_LANGUAGE, // 0% filled — dead
    EventPropertiesEnum.PAGE_TITLE, // 0% filled — dead (page_type already captures context)
    EventPropertiesEnum.ITEM_ID, // 0% filled — never instrumented

    // Facebook identifiers (not needed — widget_viewed doesn't send to FB Pixel)
    EventPropertiesEnum.FBC, // Facebook click ID
    EventPropertiesEnum.FBP, // Facebook browser ID
  ]),

  /**
   * WIDGET_CLICKED — Same schema as WIDGET_VIEWED
   * Both events fire from the same useElementEventsTracker hook with identical property needs
   */
  [EventNamesEnum.WIDGET_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.APP_DISPLAY_LANGUAGE,
    EventPropertiesEnum.PAGE_TITLE,
    EventPropertiesEnum.ITEM_ID,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
  ]),

  /**
   * CUSTOMER_SUPPORT_CLICKED — 22,842 rows/90d (web), 126,325 (app unified)
   * Validated against RAW_PROD.EVENTS_WEB.CUSTOMER_SUPPORT_CLICKED_WEB
   * No Facebook sends (sendTo defaults to []); Firebase/GA4 via default send
   * 6 call sites: ContactPhoneLink.tsx, StageOnTvClient.tsx, HomeFooter.tsx,
   *               ContactUs.tsx, WhatsAppSupport.tsx, PaymentStatus.tsx
   * Keep: source (100%), mode (100%), support_type (15.6% on app)
   */
  [EventNamesEnum.CUSTOMER_SUPPORT_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_IDS,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.CONTENT_DIALECT,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
    EventPropertiesEnum.SHOW_FORMAT,
  ]),

  /**
   * VOLUME_BUTTON_CLICKED — 2,175 rows/90d (web), 25 rows (TV orphan)
   * Validated against RAW_PROD.EVENTS_WEB.VOLUME_BUTTON_CLICKED_WEB
   * No Facebook sends (sendTo defaults to []); Firebase/GA4 via default send
   * 1 call site: CarouselCSR.tsx — home carousel platter mute/unmute button
   * Keep: content_type (100%), content_id (100%), audio_unmuted (88.4%), audio_muted (11.6%)
   */
  [EventNamesEnum.VOLUME_BUTTON_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_IDS,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.CONTENT_DIALECT,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
    EventPropertiesEnum.SHOW_FORMAT,
  ]),

  /**
   * WATCHLIST_ITEM_REMOVAL_SUCCESSFUL — 6,833 rows/90d (web), 67,664 (app)
   * Validated against RAW_PROD.EVENTS_WEB.WATCHLIST_ITEM_REMOVAL_SUCCESSFUL_WEB
   * No Facebook sends (sendTo: [] explicit); Firebase/GA4 via default send
   * 1 call site: useMyList.ts hook — fires on successful watchlist item removal
   * Keep: content_id (90.4% web — primary payload: which content was removed)
   * Note: content_ids (global FB array, 9.6% web) excluded; content_id (singular) kept
   */
  [EventNamesEnum.WATCHLIST_ITEM_REMOVAL_SUCCESSFUL]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_IDS,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.CONTENT_DIALECT,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
    EventPropertiesEnum.SHOW_FORMAT,
  ]),

  /**
   * FULL_SCREEN_CLICKED — 4,511 rows/90d (web)
   * Validated against RAW_PROD.EVENTS_WEB.FULL_SCREEN_CLICKED_WEB
   * No Facebook sends (sendTo defaults to []); Firebase/GA4 via default send
   * 1 call site: Title.tsx (line 654) — "View in Full Screen" button on title page
   * Keep: page_name (100%)
   */
  [EventNamesEnum.FULL_SCREEN_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_IDS,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.CONTENT_DIALECT,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
    EventPropertiesEnum.SHOW_FORMAT,
  ]),

  /**
   * WATCH_NOW_CLICKED — 4,247 rows/90d (web)
   * Validated against RAW_PROD.EVENTS_WEB.WATCH_NOW_CLICKED_WEB
   * No Facebook sends (sendTo defaults to []); Firebase/GA4 via default send
   * 1 call site: CarouselCSR.tsx (line 767) — "Watch Now" CTA on home carousel platter
   * Keep: content_id (45.2%), page_name (100%)
   */
  [EventNamesEnum.WATCH_NOW_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_IDS,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.CONTENT_DIALECT,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
    EventPropertiesEnum.SHOW_FORMAT,
  ]),

  /**
   * TITLE_OFFER_CLICKED — 6,122 rows/90d (web)
   * Validated against RAW_PROD.EVENTS_WEB.TITLE_OFFER_CLICKED_WEB
   * No Facebook sends (sendTo: [] explicit); Firebase/GA4 via default send
   * 1 call site: ExpiresInTimer.tsx (line 192) — spin-the-wheel offer widget click
   * Keep: no event-specific props (empty {}); global content_id (100%) retained for
   *        attribution context (which content user was viewing when offer was claimed)
   */
  /**
   * CTA_CLICKED — unified CTA event replacing WATCH_NOW_CLICKED, START_WATCHING_CLICKED,
   * EPISODE_TILE_CLICKED, SEASON_CTA_CLICKED, TRAILER_PLAY_CTA_CLICKED across all surfaces.
   * Some call sites send to Facebook (TV: ContentDetailPageItemsTv, EpisodeListingTv,
   * TrailerWidgetTv) — so fbc, fbp, content_ids, ip are intentionally KEPT.
   * Keep: cta_name, source_surface, page_source, content_id, content_slug, content_type,
   *        content_title, root_widget_*, fbc, fbp, content_ids, ip, mandate_status,
   *        user_culture, dialect, country (FB attribution + analytics context)
   */
  [EventNamesEnum.CTA_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.SHOW_FORMAT,
  ]),

  [EventNamesEnum.TITLE_OFFER_CLICKED]: Object.freeze([
    EventPropertiesEnum.CUSTOMER_USER_ID,
    EventPropertiesEnum.EXTERNAL_ID,
    EventPropertiesEnum.PH,
    EventPropertiesEnum.USER_ID_LEGACY,
    EventPropertiesEnum.LATEST_CONTENT_ID,
    EventPropertiesEnum.ECOMMERCE,
    EventPropertiesEnum.CONTENT_IDS,
    EventPropertiesEnum.CONTENT_NAME,
    EventPropertiesEnum.CONTENT_DIALECT,
    EventPropertiesEnum.APP_LANGUAGE,
    EventPropertiesEnum.COUNTRY,
    EventPropertiesEnum.CURRENCY,
    EventPropertiesEnum.NEXT_JS_WEBSITE,
    EventPropertiesEnum.IP_ADDRESS,
    EventPropertiesEnum.SDK_VERSION,
    EventPropertiesEnum.CHANNEL,
    EventPropertiesEnum.DEVICE_SCREEN_TYPE,
    EventPropertiesEnum.CONTENT_CULTURE,
    EventPropertiesEnum.INITIAL_USER_CULTURE,
    EventPropertiesEnum.MANDATE_STATUS,
    EventPropertiesEnum.USER_CULTURE,
    EventPropertiesEnum.FBC,
    EventPropertiesEnum.FBP,
    EventPropertiesEnum.SHOW_FORMAT,
  ]),
});

/**
 * Apply property exclusions for a given event.
 * Mutates the properties object in-place by deleting excluded keys.
 *
 * @param eventName - The event name (without platform suffix)
 * @param properties - The event properties object to filter
 * @returns The same properties object (for chaining)
 *
 * @example
 * ```ts
 * const props = { user_id: "123", fbc: "fb.1.xxx", content_id: 507 };
 * applyPropertyExclusions(EventNamesEnum.THUMBNAIL_VIEWED, props);
 * // props.fbc is now deleted, props.user_id and props.content_id remain
 * ```
 */
export const applyPropertyExclusions = <T extends Record<string, unknown>>(
  eventName: EventNamesEnum | string,
  properties: T,
): T => {
  const exclusions = EVENT_PROPERTY_EXCLUSIONS[eventName as EventNamesEnum];
  if (!exclusions) return properties;

  for (const key of exclusions) {
    if (Object.prototype.hasOwnProperty.call(properties, key)) {
      delete properties[key];
    }
  }

  return properties;
};
